import React, { useEffect } from 'react';
import { CameraIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useFotosConferencia } from '../../hooks/useConferencias';
import { btnSecundario } from '../ui/iris';
import GaleriaFotosConferencia from './GaleriaFotosConferencia';

interface FotosConferenciaDialogProps {
  conferenciaId: number | null;
  isOpen: boolean;
  onClose: () => void;
  /** Conferência concluída — esconde Adicionar/Remover, mantém a galeria visível. */
  somenteLeitura?: boolean;
}

/**
 * Wrapper fino: só o chrome do modal (scrim + header + footer + ESC + lock de scroll). O miolo
 * (grid, upload, ciclo de vida das object URL) mora em `GaleriaFotosConferencia` (Task 8 — extraído
 * daqui pra ser reusado pelo dialog de detalhe da pendência, com `api="pendencia"`). Aqui é sempre
 * `api="nfe"` — o fluxo de operação do workspace.
 *
 * A galeria só é renderizada enquanto `isOpen` (mesma guarda de baixo) — fechar desmonta-a de
 * verdade, e é o efeito de desmonte dela que revoga as object URLs pendentes (ver comentário lá).
 * Este componente (o wrapper) é quem fica permanentemente montado no workspace, como já era antes:
 * o botão "Fotos" só alterna `isOpen`, nunca desmonta o `FotosConferenciaDialog`.
 */
const FotosConferenciaDialog: React.FC<FotosConferenciaDialogProps> = ({
  conferenciaId,
  isOpen,
  onClose,
  somenteLeitura = false,
}) => {
  // Só pro contador do header ("N fotos"). Mesma queryKey que a galeria usa por baixo — cache
  // compartilhado do React Query, sem request duplicada. Chamado incondicionalmente (regra dos
  // hooks), igual o comportamento de sempre: este wrapper fica montado o tempo todo no workspace.
  const { data: fotos } = useFotosConferencia(conferenciaId ?? undefined);

  // Fechar com ESC + travar scroll do body enquanto aberto (mesmo molde de CtesDaNfeDialog).
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onEsc);
      document.body.style.overflow = original;
    };
  }, [isOpen, onClose]);

  if (!isOpen || conferenciaId == null) return null;

  const totalFotos = fotos?.length ?? 0;

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-[var(--scrim)] backdrop-blur-[2px]" onClick={onClose} />

      {/* Mobile: full-screen. sm+: card centralizado. */}
      <div className="fixed inset-0 flex flex-col sm:items-center sm:justify-center sm:p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Fotos da conferência"
          className="relative flex h-full w-full flex-col overflow-hidden bg-surface sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-iris-lg sm:border sm:border-line-strong sm:shadow-iris-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-iris bg-accent">
                <CameraIcon className="h-4 w-4 text-white" />
              </span>
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold text-tx">Fotos da conferência</h2>
                <p className="truncate text-[11px] text-tx-3">
                  {totalFotos > 0 ? `${totalFotos} foto${totalFotos === 1 ? '' : 's'}` : 'Nenhuma foto anexada'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-iris text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx sm:h-8 sm:w-8"
              aria-label="Fechar"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Corpo: miolo extraído pra GaleriaFotosConferencia (Task 8). */}
          <GaleriaFotosConferencia conferenciaId={conferenciaId} somenteLeitura={somenteLeitura} api="nfe" />

          {/* Footer: só "Fechar" — os botões de upload agora fazem parte da galeria (corpo). */}
          <div className="flex justify-end border-t border-line bg-surface-2 px-4 py-3">
            <button type="button" onClick={onClose} className={`${btnSecundario} w-full sm:w-auto`}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FotosConferenciaDialog;
